import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getSubscriptions, deleteSubscription, deleteBulkSubscriptions, updateBulkSubscriptions, renewSubscription } from '../services/api';
import ErrorModal from '../components/ErrorModal';
import CustomSelect from '../components/CustomSelect';
import BulkUploadModal from '../components/BulkUploadModal';
import BulkEditModal from '../components/BulkEditModal';

const Subscriptions = () => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // single id or 'bulk'
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'table'
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('nextRenewalDate');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  useEffect(() => {
    loadSubscriptions();
    const interval = setInterval(loadSubscriptions, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const loadSubscriptions = async () => {
    try {
      const response = await getSubscriptions();
      setSubscriptions(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading subscriptions:', error);
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteSubscription(id);
      setDeleteConfirm(null);
      loadSubscriptions();
    } catch (error) {
      setError('Failed to delete subscription: ' + (error.response?.data?.error || error.message));
      setShowError(true);
      setDeleteConfirm(null);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setBulkActionLoading(true);
    try {
      await deleteBulkSubscriptions(selectedIds);
      setSelectedIds([]);
      setDeleteConfirm(null);
      loadSubscriptions();
    } catch (err) {
      setError('Failed to delete: ' + (err.response?.data?.error || err.message));
      setShowError(true);
      setDeleteConfirm(null);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkEditSave = async (updates) => {
    await updateBulkSubscriptions(selectedIds, updates);
    setSelectedIds([]);
    setShowBulkEdit(false);
    loadSubscriptions();
  };

  const handleMarkRenewed = async (id) => {
    try {
      await renewSubscription(id);
      loadSubscriptions();
    } catch (err) {
      setError('Failed to mark as renewed: ' + (err.response?.data?.error || err.message));
      setShowError(true);
    }
  };

  const toggleSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAllVisible = () => {
    const visibleIds = sortedSubscriptions.map((s) => s._id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...visibleIds])]);
    }
  };

  const clearSelection = () => setSelectedIds([]);

  const getDaysUntilRenewal = (renewalDate) => {
    const now = new Date();
    const renewal = new Date(renewalDate);
    const diffTime = renewal - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStatusBadge = (subscription) => {
    const daysUntil = getDaysUntilRenewal(subscription.nextRenewalDate);
    
    if (daysUntil < 0) {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
          Overdue ({Math.abs(daysUntil)} days)
        </span>
      );
    } else if (daysUntil === 0) {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400">
          Due Today
        </span>
      );
    } else if (daysUntil <= subscription.reminderDaysBefore) {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400">
          Due Soon ({daysUntil} days)
        </span>
      );
    } else {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
          Active ({daysUntil} days)
        </span>
      );
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 dark:border-blue-800 border-t-blue-600 dark:border-t-blue-400"></div>
        <p className="mt-4 text-gray-500 dark:text-gray-400">Loading subscriptions...</p>
      </div>
    );
  }

  // Filter subscriptions based on search query
  const filteredSubscriptions = subscriptions.filter(sub => {
    const query = searchQuery.toLowerCase();
    return (
      sub.name.toLowerCase().includes(query) ||
      sub.service.toLowerCase().includes(query) ||
      sub.currency.toLowerCase().includes(query) ||
      sub.estimatedPrice.toString().includes(query)
    );
  });

  // Sort subscriptions
  const sortedSubscriptions = [...filteredSubscriptions].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortField) {
      case 'name':
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
        break;
      case 'service':
        aValue = a.service.toLowerCase();
        bValue = b.service.toLowerCase();
        break;
      case 'price':
        aValue = a.estimatedPrice;
        bValue = b.estimatedPrice;
        break;
      case 'nextRenewalDate':
        aValue = new Date(a.nextRenewalDate).getTime();
        bValue = new Date(b.nextRenewalDate).getTime();
        break;
      case 'daysUntil':
        aValue = getDaysUntilRenewal(a.nextRenewalDate);
        bValue = getDaysUntilRenewal(b.nextRenewalDate);
        break;
      default:
        return 0;
    }
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const overdueCount = subscriptions.filter(s => getDaysUntilRenewal(s.nextRenewalDate) < 0).length;
  const dueSoonCount = subscriptions.filter(s => {
    const days = getDaysUntilRenewal(s.nextRenewalDate);
    return days >= 0 && days <= 7;
  }).length;
  const activeCount = subscriptions.filter(s => s.active).length;

  return (
    <div className="max-w-7xl mx-auto">
      {/* Stats Header */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Total Subscriptions</p>
              <p className="text-3xl font-bold mt-1">{subscriptions.length}</p>
            </div>
            <div className="bg-white/20 rounded-lg p-3">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Active</p>
              <p className="text-3xl font-bold mt-1">{activeCount}</p>
            </div>
            <div className="bg-white/20 rounded-lg p-3">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium">Due Soon</p>
              <p className="text-3xl font-bold mt-1">{dueSoonCount}</p>
            </div>
            <div className="bg-white/20 rounded-lg p-3">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-sm font-medium">Overdue</p>
              <p className="text-3xl font-bold mt-1">{overdueCount}</p>
            </div>
            <div className="bg-white/20 rounded-lg p-3">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Subscriptions</h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">Manage your subscription renewals</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowBulkUpload(true)}
              className="px-4 py-3 border-2 border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-400 rounded-lg font-semibold hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              Bulk Upload
            </button>
            <Link
              to="/subscriptions/create"
              className="bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-700 dark:to-indigo-700 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 dark:hover:from-blue-600 dark:hover:to-indigo-600 shadow-lg hover:shadow-xl transition-all transform hover:scale-105 flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Subscription
            </Link>
          </div>
        </div>

        {/* Search, Sort, and View Toggle */}
        <div className="flex flex-col sm:flex-row gap-4 items-center">
          {/* Search */}
          <div className="flex-1 w-full sm:w-auto">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search subscriptions..."
                className="w-full px-4 py-2 pl-10 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              />
              <svg className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Sort */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">Sort by:</label>
            <div className="w-40">
              <CustomSelect
                value={sortField}
                onChange={(e) => handleSort(e.target.value)}
                options={[
                  { value: 'nextRenewalDate', label: 'Renewal Date' },
                  { value: 'daysUntil', label: 'Days Until' },
                  { value: 'name', label: 'Name' },
                  { value: 'service', label: 'Service' },
                  { value: 'price', label: 'Price' },
                ]}
                className="text-sm py-2"
              />
            </div>
            <button
              onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
              className="p-2 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              title={`Sort ${sortDirection === 'asc' ? 'Ascending' : 'Descending'}`}
            >
              {sortDirection === 'asc' ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </button>
          </div>

          {/* View Toggle */}
          <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => setViewMode('card')}
              className={`p-2 rounded transition-colors ${
                viewMode === 'card'
                  ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
              title="Card View"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded transition-colors ${
                viewMode === 'table'
                  ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
              title="Table View"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Bulk selection action bar */}
      {selectedIds.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3 px-5 py-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl shadow-sm">
          <span className="font-semibold text-blue-800 dark:text-blue-200">
            {selectedIds.length} selected
          </span>
          <button
            onClick={clearSelection}
            className="px-3 py-1.5 text-sm font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded-lg transition-colors"
          >
            Clear
          </button>
          <div className="h-6 w-px bg-blue-200 dark:bg-blue-700" />
          <button
            onClick={() => navigate(`/subscriptions/${selectedIds[0]}/edit`)}
            className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            {selectedIds.length === 1 ? 'Edit' : 'Edit first'}
          </button>
          <button
            onClick={() => setShowBulkEdit(true)}
            className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
            Bulk edit
          </button>
          <button
            onClick={() => setDeleteConfirm('bulk')}
            className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete
          </button>
        </div>
      )}

      {filteredSubscriptions.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border-2 border-dashed border-gray-200 dark:border-gray-700 text-center py-16">
          <div className="max-w-md mx-auto">
            <div className="bg-blue-100 dark:bg-blue-900/30 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              {subscriptions.length === 0 ? 'No subscriptions yet' : 'No subscriptions found'}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              {subscriptions.length === 0 
                ? 'Get started by creating your first subscription'
                : 'Try adjusting your search or filters'}
            </p>
            {subscriptions.length === 0 && (
              <Link
                to="/subscriptions/create"
                className="inline-flex items-center bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-700 dark:to-indigo-700 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 dark:hover:from-blue-600 dark:hover:to-indigo-600 shadow-lg hover:shadow-xl transition-all"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Your First Subscription
              </Link>
            )}
          </div>
        </div>
      ) : viewMode === 'card' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedSubscriptions.map((subscription) => {
            const daysUntil = getDaysUntilRenewal(subscription.nextRenewalDate);
            const isSelected = selectedIds.includes(subscription._id);
            const isOverdue = daysUntil < 0;
            const inReminderWindow = daysUntil >= 0 && daysUntil <= subscription.reminderDaysBefore;
            return (
              <div
                key={subscription._id}
                className={`bg-white dark:bg-gray-800 rounded-xl shadow-md border-2 p-6 hover:shadow-xl transition-all ${
                  isSelected
                    ? 'border-blue-500 dark:border-blue-400 ring-2 ring-blue-200 dark:ring-blue-800'
                    : isOverdue
                    ? 'border-red-300 dark:border-red-500 ring-2 ring-red-200/60 dark:ring-red-900/60'
                    : inReminderWindow
                    ? 'border-amber-300 dark:border-amber-500 ring-2 ring-amber-200/60 dark:ring-amber-900/60'
                    : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                <div className="flex justify-between items-start mb-4">
                  <label className={`flex items-center justify-center shrink-0 mr-2 w-8 h-8 rounded-full border-2 cursor-pointer transition-all duration-200 focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 dark:focus-within:ring-offset-gray-800 ${
                    isSelected
                      ? 'border-blue-500 bg-blue-500 dark:bg-blue-500 hover:bg-blue-600 dark:hover:bg-blue-600'
                      : 'border-gray-300 dark:border-gray-500 hover:border-blue-400 dark:hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-900/20'
                  }`}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelection(subscription._id)}
                      className="sr-only peer"
                    />
                    {isSelected && (
                      <svg className="w-5 h-5 text-white pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    <span className="sr-only">Select</span>
                  </label>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    {subscription.name}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{subscription.service}</p>
                </div>
                {getStatusBadge(subscription)}
              </div>

                <div className="space-y-3 mt-4">
                  {(isOverdue || inReminderWindow) && (
                    <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700">
                      <div className="flex items-center gap-2 text-xs sm:text-sm text-amber-800 dark:text-amber-200">
                        <span className="inline-flex w-5 h-5 items-center justify-center rounded-full bg-amber-400 text-white text-[10px] font-bold">
                          !
                        </span>
                        <span>
                          {isOverdue
                            ? 'Payment overdue - update when you renew.'
                            : `Within reminder window (${daysUntil} day${daysUntil === 1 ? '' : 's'} left).`}
                        </span>
                      </div>
                      <button
                        onClick={() => handleMarkRenewed(subscription._id)}
                        className="ml-3 inline-flex items-center px-3 py-1.5 rounded-md text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm"
                      >
                        I paid / Renewed
                      </button>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Price:</span>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {subscription.currency} {subscription.estimatedPrice}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Duration:</span>
                    <span className="font-semibold text-gray-900 dark:text-white capitalize">
                      {subscription.duration}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Renewal Date:</span>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {new Date(subscription.nextRenewalDate).toLocaleDateString()}
                    </span>
                  </div>
                  {subscription.notifyEmails && subscription.notifyEmails.length > 0 && (
                    <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      {subscription.notifyEmails.length} email(s)
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => handleMarkRenewed(subscription._id)}
                    className="px-4 py-2 text-sm font-medium rounded-lg border border-emerald-500/70 text-emerald-700 dark:text-emerald-300 bg-emerald-50/60 dark:bg-emerald-900/20 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Mark renewed
                  </button>
                  <div className="ml-auto flex gap-2 flex-1 sm:flex-none justify-end">
                    <Link
                      to={`/subscriptions/${subscription._id}/edit`}
                      className="flex-1 px-4 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors text-center text-sm font-medium"
                    >
                      Edit
                    </Link>
                    <button
                      onClick={() => setDeleteConfirm(subscription._id)}
                      className="flex-1 px-4 py-2 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors text-sm font-medium"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-3 w-12">
                    {(() => {
                      const allSelected = sortedSubscriptions.length > 0 && sortedSubscriptions.every((s) => selectedIds.includes(s._id));
                      return (
                    <label className={`inline-flex items-center justify-center w-7 h-7 rounded-full border-2 cursor-pointer transition-all duration-200 focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 dark:focus-within:ring-offset-gray-800 ${
                      allSelected
                        ? 'border-blue-500 bg-blue-500 dark:bg-blue-500 hover:bg-blue-600 dark:hover:bg-blue-600'
                        : 'border-gray-300 dark:border-gray-500 hover:border-blue-400 dark:hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-900/20'
                    }`}>
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={selectAllVisible}
                        className="sr-only"
                      />
                      {allSelected && (
                        <svg className="w-4 h-4 text-white pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      <span className="sr-only">Select all</span>
                    </label>
                      );
                    })()}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-2">
                      Name
                      {sortField === 'name' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => handleSort('service')}
                  >
                    <div className="flex items-center gap-2">
                      Service
                      {sortField === 'service' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => handleSort('price')}
                  >
                    <div className="flex items-center gap-2">
                      Price
                      {sortField === 'price' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Duration
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => handleSort('nextRenewalDate')}
                  >
                    <div className="flex items-center gap-2">
                      Renewal Date
                      {sortField === 'nextRenewalDate' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => handleSort('daysUntil')}
                  >
                    <div className="flex items-center gap-2">
                      Days Until
                      {sortField === 'daysUntil' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {sortedSubscriptions.map((subscription) => {
                  const daysUntil = getDaysUntilRenewal(subscription.nextRenewalDate);
                  const isSelected = selectedIds.includes(subscription._id);
                  const isOverdue = daysUntil < 0;
                  const inReminderWindow = daysUntil >= 0 && daysUntil <= subscription.reminderDaysBefore;
                  return (
                    <tr
                      key={subscription._id}
                      className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                        isSelected
                          ? 'bg-blue-50/50 dark:bg-blue-900/20'
                          : isOverdue
                          ? 'bg-red-50/40 dark:bg-red-900/10'
                          : inReminderWindow
                          ? 'bg-amber-50/40 dark:bg-amber-900/10'
                          : ''
                      }`}
                    >
                      <td className="px-4 py-4 w-12">
                        <label className={`inline-flex items-center justify-center w-7 h-7 rounded-full border-2 cursor-pointer transition-all duration-200 focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 dark:focus-within:ring-offset-gray-800 ${
                          isSelected
                            ? 'border-blue-500 bg-blue-500 dark:bg-blue-500 hover:bg-blue-600 dark:hover:bg-blue-600'
                            : 'border-gray-300 dark:border-gray-500 hover:border-blue-400 dark:hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-900/20'
                        }`}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelection(subscription._id)}
                            className="sr-only"
                          />
                          {isSelected && (
                            <svg className="w-4 h-4 text-white pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                          <span className="sr-only">Select</span>
                        </label>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          {subscription.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {subscription.service}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          {subscription.currency} {subscription.estimatedPrice}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                          {subscription.duration}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {new Date(subscription.nextRenewalDate).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm font-semibold ${
                          daysUntil < 0 ? 'text-red-600 dark:text-red-400' :
                          daysUntil === 0 ? 'text-yellow-600 dark:text-yellow-400' :
                          daysUntil <= subscription.reminderDaysBefore ? 'text-orange-600 dark:text-orange-400' :
                          'text-green-600 dark:text-green-400'
                        }`}>
                          {daysUntil < 0 ? `${Math.abs(daysUntil)} days ago` : `${daysUntil} days`}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(subscription)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleMarkRenewed(subscription._id)}
                            className={`px-3 py-1 rounded-lg text-xs font-semibold border ${
                              isOverdue || inReminderWindow
                                ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-500/70 hover:bg-emerald-100 dark:hover:bg-emerald-900/50'
                                : 'bg-white dark:bg-gray-800 text-emerald-700 dark:text-emerald-300 border-emerald-500/50 hover:bg-emerald-50 dark:hover:bg-emerald-900/30'
                            }`}
                          >
                            Mark renewed
                          </button>
                          <Link
                            to={`/subscriptions/${subscription._id}/edit`}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                          >
                            Edit
                          </Link>
                          <button
                            onClick={() => setDeleteConfirm(subscription._id)}
                            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {subscriptions.length > 0 && filteredSubscriptions.length === 0 && (
        <div className="mt-4 text-center text-sm text-gray-500 dark:text-gray-400">
          No subscriptions match your search "{searchQuery}"
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              {deleteConfirm === 'bulk' ? `Delete ${selectedIds.length} subscriptions?` : 'Delete subscription?'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {deleteConfirm === 'bulk'
                ? `This will permanently delete ${selectedIds.length} subscription(s). This cannot be undone.`
                : 'Are you sure you want to delete this subscription? This action cannot be undone.'}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={bulkActionLoading}
                className="flex-1 px-4 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteConfirm === 'bulk' ? handleBulkDelete() : handleDelete(deleteConfirm)}
                disabled={bulkActionLoading}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 font-semibold"
              >
                {bulkActionLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <BulkUploadModal
        isOpen={showBulkUpload}
        onClose={() => setShowBulkUpload(false)}
        onSuccess={loadSubscriptions}
      />

      <BulkEditModal
        isOpen={showBulkEdit}
        onClose={() => setShowBulkEdit(false)}
        count={selectedIds.length}
        onSave={handleBulkEditSave}
      />

      <ErrorModal
        isOpen={showError}
        onClose={() => setShowError(false)}
        title="Error"
        message={error || ''}
      />
    </div>
  );
};

export default Subscriptions;
